/* eslint-disable no-unsafe-optional-chaining */
import {
 Box,
 Chip,
 IconButton,
 LinearProgress,
 linearProgressClasses,
 Theme,
 useTheme,
 Tooltip,
 Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import { t } from 'i18next';
import { MutableRefObject, useEffect, useState } from 'react';

import { titleStyles } from '../../common/InfoSidebar/infoStyles';
import LazySvg from '../../common/LazySvg';
import { CustomGridApi } from '../../common/XTable/types';
import InfoFavorites from './InfoFavorites';
import InfoMainEdit from './InfoMainEdit';

/* eslint-disable @typescript-eslint/no-explicit-any */
interface InfoMainProps {
 apiRef: MutableRefObject<CustomGridApi>;
 data: any;
}

const InfoMain = (props: InfoMainProps) => {
 const { apiRef, data } = props;
 const [isEdit, setIsEdit] = useState(false);
 const th = useTheme();
 useEffect(() => {
  setIsEdit(false);
 }, [data?.Id]);

 const weightData = [
  {
   label: t('results.bruto'),
   value: data?.Bruto?.Weight,
   date: data?.Bruto?.Time,
   doc: data?.BrutoDoc,
   docDiff:
    typeof data?.Bruto?.Weight === 'number' &&
    typeof data?.BrutoDoc === 'number' &&
    !Number.isNaN(data.Bruto?.Weight) &&
    !Number.isNaN(data.BrutoDoc) &&
    data.BrutoDoc !== 0
     ? ((data.Bruto?.Weight - data.BrutoDoc) / data.BrutoDoc) * 100
     : null,
  },

  {
   label: t('results.tare'),
   value: data?.Tare?.Weight,
   date: data?.Tare?.Time,
  },
  {
   doc: data?.NetoDoc,
   label: t('results.net'),
   value: data?.Neto,
   docDiff:
    typeof data?.Neto === 'number' &&
    typeof data?.NetoDoc === 'number' &&
    !Number.isNaN(data.Neto) &&
    !Number.isNaN(data.NetoDoc) &&
    data.NetoDoc !== 0
     ? ((data.Neto - data.NetoDoc) / data.NetoDoc) * 100
     : null,
  },
 ];

 const productsData =
  data?.ResultProducts?.map((product: any) => ({
   label: `${product?.Product.Name}${
    product.Product?.ShortCode ? ` (${product.Product?.ShortCode})` : ''
   }`,
   //    total: 10000,
   net: '',
   perc: product.Percentage,
  })) || [];
 const carNumber = data?.Car?.Number;
 const trailerNumber = data?.Trailer1?.Number;
 const carType = data?.Car?.Type;
 const driverFullName = [data?.Driver?.NameFirst, data?.Driver?.NameLast]
  .filter(Boolean)
  .join(' ');
 return (
  <>
   <InfoFavorites data={data} />
   <Typography sx={{ ...titleStyles, mb: '12px' }}>
    {t('results.weight')}
   </Typography>

   <Box
    sx={{
     mb: '20px',
     background: (theme: Theme) => theme.palette.kemek.lightBlue,
     borderRadius: '4px',
     p: 1,
     display: 'flex',
     //  alignItems: 'flex-start',
     gap: '4px',
    }}
   >
    {weightData.map((item, index) => (
     <Box
      key={item.label}
      sx={{
       display: 'flex',
       flexDirection: 'column',
       justifyContent: 'space-between',
       alignItems: 'center',
       gap: 1,
       flex: 1,
      }}
     >
      {weightData.filter((item: any) => item?.date).length > 0 && (
       <Box
        sx={{
         display: 'flex',
         justifyContent: 'space-between',
         px: '4px',
         width: '100%',
         minHeight: '15px',
        }}
       >
        {/* date */}
        {item.date && (
         <>
          <Typography
           sx={{
            fontSize: 10,
            fontWeight: 500,
            color: (theme: Theme) => theme.palette.kemek.gray,
            whiteSpace: 'nowrap',
           }}
          >
           {dayjs(item.date).format('MM-DD')}
          </Typography>
          <Typography
           sx={{
            fontSize: 10,
            fontWeight: 500,
            whiteSpace: 'nowrap',
            color: (theme: Theme) => theme.palette.kemek.darkGray,
           }}
          >
           {dayjs(item.date).format('HH:mm:ss')}
          </Typography>
         </>
        )}
       </Box>
      )}
      <Box
       sx={{
        textAlign: 'center',
        background: (theme: Theme) => theme.palette.kemek.white,
        borderRadius: '4px',
        px: '12px',
        py: '4px',
        width: '100%',
        // width: 100,
        minHeight: '54px',
       }}
      >
       {/* value */}
       <Box
        sx={{
         fontSize: 12,
         fontWeight: 500,
         color: (theme: Theme) => theme.palette.kemek.darkGray,
         mb: '4px',
         display: 'flex',
         alignItems: 'center',
         justifyContent: 'center',
         gap: '4px',
        }}
       >
        {item.label}
        {item.docDiff && (
         <Chip
          size="small"
          // color={item.docDiff > 0 ? 'success' : 'error'}
          sx={{
           fontSize: 9,
           height: '18px',
           background: (theme: Theme) => theme.palette.kemek.lightBlue,
          }}
          label={`${(item?.docDiff || 0) > 0 ? '+' : ''}${item.docDiff?.toFixed(
           0,
          )}%`}
         />
        )}
       </Box>
       <Box
        sx={{
         display: 'flex',
         alignItems: 'center',
         justifyContent: 'center',
         gap: '4px',
        }}
       >
        <Typography
         sx={{
          fontSize: 16,
          color: (theme: Theme) =>
           item?.label === t('results.net')
            ? theme.palette.kemek.blue
            : theme.palette.kemek.darkestGray,
         }}
        >
         {item.value ? item.value.toLocaleString('fr-FR') : ''}
        </Typography>
       </Box>
      </Box>

      {weightData.filter((item: any) => item?.doc).length > 0 && (
       <Box sx={{ textAlign: 'center', minHeight: '24px' }}>
        {/* doc */}
        {item.doc && (
         <Tooltip title={t('results.docWeight')}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
           <LazySvg
            name="paper"
            styles={{
             '& path': { stroke: th.palette.kemek.darkGray }, // darkGray
            }}
           />
           <Typography
            sx={{
             fontSize: 10,
             fontWeight: 500,
             color: (theme: Theme) => theme.palette.kemek.darkGray,
            }}
           >
            {item.doc ? item.doc.toLocaleString('fr-FR') : ''}
           </Typography>
          </Box>
         </Tooltip>
        )}
       </Box>
      )}
     </Box>
    ))}
   </Box>

   {/* products */}
   {productsData?.length > 0 && (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mb: '20px' }}>
     {productsData?.map((item: any, index: number) => (
      <Box key={item?.label} sx={{ display: 'flex', flexDirection: 'column' }}>
       <Box sx={{ width: '100%', display: 'flex', mb: '4px' }}>
        <Typography sx={{ fontSize: 12 }}>{item?.label}</Typography>
        <Typography sx={{ ml: 'auto' }}>{item?.net}</Typography>
        <Typography
         sx={{
          color: (theme: Theme) => theme.palette.kemek.gray,
          fontSize: 12,
          ml: '12px',
         }}
        >
         {item?.perc ? `${item?.perc}%` : ''}
        </Typography>
       </Box>

       <Box sx={{ width: '100%', mr: 1 }}>
        <LinearProgress
         sx={{
          borderRadius: 4,
          backgroundColor: (theme: Theme) => theme.palette.kemek.lightestGray,
          [`& .${linearProgressClasses.bar1Determinate}`]: {
           borderRadius: 4,
           backgroundColor: (theme: Theme) =>
            [
             theme.palette.kemek.blue,
             theme.palette.kemek.green,
             theme.palette.kemek.orange,
             theme.palette.kemek.yellow,
             theme.palette.kemek.red,
            ][index % 5],
          },
         }}
         variant="determinate"
         value={item?.perc}
        />
       </Box>
      </Box>
     ))}
    </Box>
   )}

   {/* transport */}
   <Box
    sx={{
     display: 'flex',
     alignItems: 'center',
     justifyContent: 'space-between',
     mb: '12px',
    }}
   >
    <Typography sx={{ ...titleStyles, mb: 0 }}>
     {t('results.driver')}
    </Typography>
    <IconButton onClick={() => setIsEdit((prevState) => !prevState)}>
     <LazySvg name="edit" />
    </IconButton>
   </Box>
   {isEdit ? (
    <InfoMainEdit
     apiRef={apiRef}
     setIsEdit={setIsEdit}
     id={data?.Id}
     car={data?.Car}
     driver={data?.Driver}
     trailer={data?.Trailer1}
    />
   ) : (
    <Box
     sx={{
      display: 'flex',
      justifyContent: 'space-between',
      background: (theme: Theme) => theme.palette.kemek.lightBlue,
      borderRadius: '4px',
      px: 2,
      py: '12px',
      mb: '20px',
      gap: '12px',
     }}
    >
     {carNumber || trailerNumber ? (
      <Box
       sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
       }}
      >
       {carNumber ? (
        <Box
         sx={{
          display: 'flex',
          alignItems: 'center',
          fontSize: 16,
          //  mb: 1,
          wordBreak: 'break-all',
          minHeight: '24px',
         }}
        >
         <>
          <LazySvg name="scales" styles={{ marginRight: '4px ' }} />
          {carNumber}
         </>
        </Box>
       ) : null}
       {trailerNumber ? (
        <Box
         sx={{
          display: 'flex',
          alignItems: 'center',
          color: (theme: Theme) => theme.palette.kemek.darkGray,
          wordBreak: 'break-all',
          minHeight: '24px',
         }}
        >
         <>
          <LazySvg name="trailer" styles={{ marginRight: '8px' }} />
          {trailerNumber}
         </>
        </Box>
       ) : null}
      </Box>
     ) : null}
     {carType || driverFullName ? (
      <Box
       sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        textAlign: 'right',
       }}
      >
       {carType ? (
        <Box
         sx={{
          wordBreak: 'break-all',
          color: (theme: Theme) => theme.palette.kemek.darkGray,
          minHeight: '24px',
         }}
        >
         {carType}
        </Box>
       ) : null}
       {driverFullName ? (
        <Box
         sx={{
          wordBreak: 'break-all',
          minHeight: '24px',
          pt: '2px', // fix it
          //  alignSelf: 'flex-end',
         }}
        >
         {driverFullName}
        </Box>
       ) : null}
      </Box>
     ) : null}
    </Box>
   )}
  </>
 );
};

export default InfoMain;

